import { type CartEntryPartDTO, type CartViewDTO, type CartViewEntryDTO, type LineItemDTO } from "../dto/CartViewDTO";
import {
  type CartEntry,
  type LineItem,
  PackageEntrySource,
  type PlainLineItem,
  type SubLineItem,
} from "../models/CartData";
import * as WeightUtils from "../../../../utils/WeightUtils";
import { type ProductData } from "../../../../models/ProductData";
import { transformLinear, transformProduct } from "../../../../ProductDetails/transformers/ProductDataTransformer";
import { getNextUID } from "../../../../utils/uid";
import { toMapFn } from "../../../../utils/reducers";
import { type LineItemMessage } from "../../../../utils/cartUpdating/common";
import dayjs from "dayjs";
import { type NetPriceData } from "~/models/PriceData";
import { isSubscription } from "~/utils/productUtils";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import { INVALID_ENTRY_ID } from "~/QuickEntryPopup/constants";
import { isCartPackEntryValid } from "~/feature/cart/cart/redux/reducers/lineItemValidations";

export interface TransformedCartData {
  accountNumber: string;
  cartEntries: number[];
  cartEntriesById: { [id: number]: CartEntry };
  packEntriesById: { [id: number]: LineItem };
  productByCode: { [productCode: string]: ProductData };
  dstError: boolean;
  comment: string;
}

export function transformEntries(dtoEntries: CartViewEntryDTO[] | null | undefined) {
  let entries = dtoEntries ?? [];

  // Sanity check - remove invalid entries
  entries = entries.filter((entry) => entry.packageEntries.length > 0);

  const data = entries.map((entry) => {
    const product = entry.productLinearInfo
      ? transformLinear(entry.product, entry.productLinearInfo)
      : transformProduct(entry.product);
    const { cartEntry, packageEntries, subLineItems, subProducts } = transformEntry(entry, product);
    return {
      product,
      cartEntry,
      packageEntries,
      subLineItems,
      subProducts,
    };
  });
  const productByCode = toMapFn(
    data,
    (data) => data.product.code,
    (data) => data.product,
  );
  const cartEntries = data.map((item) => item.cartEntry);
  const packageEntries = data.map((item) => item.packageEntries).flat();
  const subLineItems = data.map((item) => item.subLineItems).flat();
  const subProducts = data.map((item) => item.subProducts).flat();

  const cartEntriesByProductCode = toMapFn(cartEntries, (x) => x.productCode);

  subLineItems.forEach((subLineItem) => {
    let cartEntry = cartEntriesByProductCode[subLineItem.productCode];
    if (!cartEntry) {
      // Fake cart entry for product that exists only as a sub-line item
      cartEntry = {
        pk: -1,
        id: getNextUID(),
        entryNumber: INVALID_ENTRY_ID,
        productCode: subLineItem.productCode,
        packEntryIds: [],
      };
      cartEntriesByProductCode[subLineItem.productCode] = cartEntry;
      cartEntries.push(cartEntry);
    }
    cartEntry.packEntryIds.push(subLineItem.id);
    subLineItem.cartEntryId = cartEntry.id;
  });

  packageEntries.push(...subLineItems);

  subProducts.forEach((product) => (productByCode[product.code] = product));

  return {
    cartEntries,
    packageEntries,
    productByCode,
  };
}

export function transform(dto: CartViewDTO): TransformedCartData {
  const { cartEntries, packageEntries, productByCode } = transformEntries(dto.entries);

  const accountNumber = getAccountNumber(dto.b2bCustomerData);

  return {
    accountNumber,
    cartEntries: cartEntries.map((entry) => entry.id),
    cartEntriesById: toMapFn(cartEntries, (entry) => entry.id),
    packEntriesById: toMapFn(packageEntries, (entry) => entry.id),
    productByCode,
    dstError: dto.dstError,
    comment: dto.affiliateComment ?? "",
  };
}

export function getAccountNumber(dto: CartViewDTO["b2bCustomerData"]): string {
  return (dto && dto.sessionSelectedAccount && dto.sessionSelectedAccount.displayUid) || "";
}

function transformEntry(
  dto: CartViewEntryDTO,
  product: ProductData,
): { cartEntry: CartEntry; packageEntries: LineItem[]; subLineItems: SubLineItem[]; subProducts: ProductData[] } {
  const cartEntryId = getNextUID();
  const cartEntryNumber = dto.entryNumber;
  const cartEntryPk = dto.pk;
  const { lineItems, subLineItems, products } = (dto.packageEntries || [])
    .map((entry) => transformPackageEntry(cartEntryId, cartEntryNumber, cartEntryPk, entry, product))
    .reduce(
      (acc, item) => {
        acc.lineItems.push(item.lineItem);
        acc.subLineItems.push(...item.subLineItems);
        acc.products.push(...item.products);
        return acc;
      },
      { lineItems: [], subLineItems: [], products: [] } as {
        lineItems: LineItem[];
        subLineItems: SubLineItem[];
        products: ProductData[];
      },
    );

  return {
    cartEntry: {
      pk: dto.pk,
      productCode: product.code,
      packEntryIds: lineItems.map((entry) => entry.id),
      entryNumber: cartEntryNumber,
      id: cartEntryId,
    },
    packageEntries: lineItems,
    subLineItems,
    subProducts: products,
  };
}

function transformCartEntryPart(
  cartEntryPart: CartEntryPartDTO,
  parentLineItem: LineItem,
): { subLineItems: SubLineItem[]; product: ProductData } {
  const product = transformProduct(cartEntryPart.product);
  const subLineItems = cartEntryPart.packageEntries.map((item) => {
    const subLineItem = transformPackageEntry(-1, null, null, item, product).lineItem as SubLineItem;
    subLineItem.parentLineItemId = parentLineItem.id;
    subLineItem.priceReference = parentLineItem.priceReference;
    subLineItem.optionalPartCode = cartEntryPart.code;
    subLineItem.source = parentLineItem.source;
    subLineItem.entryNumber = -1;
    return subLineItem;
  });
  return {
    subLineItems: subLineItems,
    product,
  };
}

function transformPackageEntry(
  cartEntryId: number,
  cartEntryNumber: number | null,
  cartEntryPk: number | null,
  dto: LineItemDTO,
  product: ProductData,
): {
  lineItem: LineItem;
  subLineItems: SubLineItem[];
  products: ProductData[];
} {
  const requestedDeliveryDate =
    dto.selectedDeliveryDate && dto.selectedDeliveryDateIsManual ? new Date(dto.selectedDeliveryDate) : new Date();
  // const estimatedDeliveryDate: EstimatedDeliveryDate|null =
  //     dto.estimatedDeliveryDate ? {type: 'estimate', date: new Date(dto.estimatedDeliveryDate)} : null;

  const weight = product.weight ?? WeightUtils.zero();

  const isBulkUploadEntry = !dto.pnaCall;

  const messages: LineItemMessage[] = [];

  const quoteExpiryDate = dto.quoteExpiryDate ? new Date(dto.quoteExpiryDate) : null;
  if (quoteExpiryDate && dayjs(quoteExpiryDate).isBefore(new Date(), "day")) {
    messages.push({
      type: "fatal",
      key: "page.cart.message.error.quoteExpired",
    });
  }

  // TODO: Temporary fix - BE should always provide the value correctly
  let source: PackageEntrySource;
  if (dto.quoteNumber) {
    source = PackageEntrySource.QUOTE;
  } else if (dto.contractNumber) {
    source = PackageEntrySource.CONTRACT;
  } else {
    source = dto.source;
  }

  let netPrice: NetPriceData | undefined = undefined;
  if (!isBulkUploadEntry && dto.netPrice) {
    const surcharge = dto.surcharge ?? 0;
    const tariffSurcharge = dto.isTariffSurchargeApplicable;
    const value = dto.netPrice.value;
    const baseValue = isSurchargeIncludedInPriceValue() ? value : value / (1 + surcharge);
    netPrice = {
      value,
      baseValue,
      surcharge,
      tariffSurcharge,
      currencyIso: dto.netPrice.currencyIso,
      unit: dto.priceUnit ?? 1,
      isSubscription: isSubscription(product),
    };
  }

  const item: LineItem = {
    pk: dto.pk,
    id: getNextUID(),
    cartEntryId,

    productCode: product.code,
    comment: dto.comment,
    deliveryDate: requestedDeliveryDate,
    manuallySetRDD: dto.selectedDeliveryDateIsManual ?? false,
    packSize: dto.packageSize,
    packCode: dto.packageCode,
    quantity: dto.quantity,
    weight: weight,
    netPrice,
    valid: true,
    entryNumber: dto.entryNumber,
    primaryWarehouseName: dto.primaryWarehouseName || "",
    primaryWarehouseCode: dto.primaryWarehouseCode || "",
    messages,
    disableEdit: false,
    source,
    showComments: false,
    priceReference: dto.priceReference || "",

    // edd: estimatedDeliveryDate,
    edd: null,
    isInStock: null,

    // Salesman fields
    internalComment: "",
    priceOverriden: false,

    // Constract fields
    contractLine: dto.contractLine || "",
    contractNumber: dto.contractNumber || "",

    // Quote fields
    quoteLine: makeQuoteLineNumber(dto.quoteLineNumber, dto.quoteScheduleLineNum),
    quoteNumber: dto.quoteNumber ?? "",
    minimumQty: dto.quoteMinQuantity ?? 1,
    isBundled: dto.isBundled ?? false,
    original: dto.original ?? true,
    estimatedShippingDate: dto.estimatedShippingDate ? new Date(dto.estimatedShippingDate) : null,
    logisticalComment: dto.logisticalComment ?? "",
    quoteExpiryDate: dto.quoteExpiryDate ? new Date(dto.quoteExpiryDate) : null,

    // UI Fields
    isPackagingSelectionOpen: false,

    restrictedPackage: dto.isRestrictedPackage ?? false,

    optionalPartIds: [],

    customerOrderNumber: dto.customerOrderNumber ?? "",
    countryOfOrigin: dto.originCountry ?? "",

    // Sales campaign fields
    saleCampaignCode: dto.salesCampaignCode,
    saleCampaignEnabled: dto.specialPrice,
    salePrice: dto.saleNetPrice,
    actualNetPrice: dto.actualNetPrice,
    actualSalePrice: dto.actualSalePrice,
    // Product recommendations
    recoPlace: dto.recommendationPlace,
    recoType: dto.recommendationType,
  } as unknown as LineItem;

  item.cartEntryPk = cartEntryPk !== null ? cartEntryPk : INVALID_ENTRY_ID;
  item.cartEntryNumber = cartEntryNumber !== null ? cartEntryNumber : INVALID_ENTRY_ID;

  const { subLineItems, products } = (dto.optionalParts ?? [])
    .map((optionalPart) => transformCartEntryPart(optionalPart, item))
    .reduce(
      (acc, item) => {
        acc.subLineItems.push(...item.subLineItems);
        acc.products.push(item.product);
        return acc;
      },
      { subLineItems: [], products: [] } as { subLineItems: SubLineItem[]; products: ProductData[] },
    );

  (item as PlainLineItem).optionalPartIds = subLineItems.map((i) => i.id);

  const childrenValid = subLineItems.every((item) => item.valid);
  item.valid = childrenValid && isCartPackEntryValid(item);

  return {
    lineItem: item,
    subLineItems,
    products,
  };
}

const makeQuoteLineNumber = (quoteLineNo: string | null, scheduleLineNo: string | null) => {
  quoteLineNo = quoteLineNo ?? "";
  scheduleLineNo = scheduleLineNo ?? "";
  if (!quoteLineNo || !scheduleLineNo) {
    return "";
  }
  return `${quoteLineNo}:${scheduleLineNo}`;
};
